import { useLocation, Link } from "react-router-dom";
import { Home, AlertTriangle, Map as MapIcon, Heart, UserRound, Lightbulb } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import { useUnreadCounters } from "@/lib/useUnreadCounters";

/**
 * BottomTabBar — 5 tabs, matches mobile app exactly:
 * Home · Reports · Map · Projects · Profile
 * In RTL, flex direction reverses naturally so order on screen is
 * (right → left): Home, Reports, Map, Projects, Account.
 */
export function BottomTabBar() {
  const loc = useLocation();
  const { user } = useAuth();
  const { language } = useI18n();
  const path = loc.pathname;
  const { messages: unreadMsgs, notifications: unreadNotifs } = useUnreadCounters(!!user);
  const accountBadge = (unreadMsgs || 0) + (unreadNotifs || 0);

  const tabs = [
    { key: "home",     to: "/dashboard",          icon: Home,          label: t("tab_home", language),     match: ["/dashboard"] },
    { key: "reports",  to: "/reports",            icon: AlertTriangle, label: t("tab_reports", language),  match: ["/reports"] },
    { key: "ideas",    to: "/ideas",              icon: Lightbulb,     label: language === "ar" ? "أفكار" : language === "he" ? "רעיונות" : "Ideas", match: ["/ideas"] },
    { key: "map",      to: "/map",                icon: MapIcon,       label: t("tab_map", language),      match: ["/map"] },
    { key: "projects", to: "/community",          icon: Heart,         label: t("tab_projects", language), match: ["/community", "/projects"] },
    { key: "account",  to: `/profile/${user?.id || ""}`, icon: UserRound, label: t("tab_account", language), match: ["/profile", "/settings", "/messages", "/notifications"] },
  ];

  return (
    <nav
      data-testid="mobile-bottom-tab"
      className="md:absolute md:bottom-0 fixed bottom-0 left-0 right-0 md:left-auto md:right-auto md:w-full bg-white border-t border-[var(--te-divider)] z-40"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      <div className="flex items-stretch justify-around h-16 max-w-md mx-auto px-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = tab.match.some((m) => path.startsWith(m));
          const showBadge = tab.key === "account" && accountBadge > 0;
          return (
            <Link
              key={tab.key}
              to={tab.to}
              data-testid={`tab-${tab.key}`}
              className="flex flex-col items-center justify-center gap-0.5 flex-1 transition-colors group relative min-w-0"
            >
              <div className="relative">
                <Icon
                  size={22}
                  strokeWidth={active ? 2.4 : 1.8}
                  className={active ? "text-[var(--te-primary)]" : "text-[#A0A6B0]"}
                />
                {showBadge && (
                  <span
                    data-testid="tab-account-badge"
                    className="absolute -top-1 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[var(--te-danger)] text-white text-[9px] font-bold flex items-center justify-center"
                  >
                    {accountBadge > 99 ? "99+" : accountBadge}
                  </span>
                )}
              </div>
              <span
                className={`text-[10px] font-semibold truncate w-full text-center ${active ? "text-[var(--te-primary)]" : "text-[#A0A6B0]"}`}
              >
                {tab.label}
              </span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
